import { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, Accordion, AccordionSummary, AccordionDetails, Chip, Grid, Modal } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

function Events() {
  const [events, setEvents] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const q = query(collection(db, 'events'), orderBy('date', 'asc'));
      const querySnapshot = await getDocs(q);
      const eventsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date.toDate(),
      }));
      
      // Group events by date
      const groupedEvents = eventsList.reduce((acc, event) => {
        const dateKey = format(event.date, 'EEEE dd MMM');
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push({
          time: format(event.date, 'h:mm a'),
          title: event.title,
          type: ['In-person'], // You can modify this based on your needs
          speaker: event.organizer,
          location: event.location,
          description: event.description,
          fullEvent: event,
        });
        return acc;
      }, {});

      const eventSchedule = Object.keys(groupedEvents).map(date => ({
        date,
        events: groupedEvents[date],
      }));

      // Get the first 3 upcoming events as featured events
      const upcomingEvents = eventsList.filter(event => event.date >= new Date()).slice(0, 3);
      
      setEvents(eventSchedule);
      setFeaturedEvents(upcomingEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleOpen = (event) => {
    setSelectedEvent(event);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedEvent(null);
  };

  return (
    <Box sx={{ mt: '-64px', pt: '64px' }}>
      {/* Hero Banner */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '200px', md: '250px' },
          width: '100%',
          overflow: 'hidden',
          mb: 0,
          bgcolor: '#580100',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/events-banner.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)',
            zIndex: 0
          }
        }}
      >
        <Container 
          maxWidth="lg"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: 'white',
              textAlign: 'center',
              fontWeight: 700,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Events
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, background: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          {/* Main Content */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start', mb: 12 }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}
              >
                Discover
              </Typography>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={{ fontWeight: 700, mb: 4, color: '#580100' }}
              >
                Join Our Events
              </Typography>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body1" 
                sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.7 }}
              >
                Explore our exciting lineup of events and stay updated with the latest
                happenings in the legal community. From informative seminars to
                networking opportunities, our events page offers a variety of engaging
                experiences for professionals and enthusiasts alike.
              </Typography>
            </Box>
          </Box>

          {/* Featured Events Section */}
        

          {/* Event Schedule Section */}
          <Box sx={{ mb: 12 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 700, 
                mb: 4, 
                textAlign: 'center',
                color: 'primary.main'
              }}
            >
              Event Schedule
            </Typography>

            {events.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8, 
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="h6" color="text.secondary">
                  No upcoming events scheduled
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check back later for new events
                </Typography>
              </Box>
            ) : (
              events.map((day, index) => (
                <Accordion
                  key={index}
                  sx={{
                    mb: 2,
                    '&:before': { display: 'none' },
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                    '&.Mui-expanded': {
                      margin: '0 0 16px 0',
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '& .MuiAccordionSummary-content': {
                        my: 2,
                      },
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: 'primary.main'
                      }}
                    >
                      {day.date}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {day.events.map((event, eventIndex) => (
                      <Box
                        key={eventIndex}
                        sx={{
                          p: 3,
                          borderBottom: eventIndex !== day.events.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.01)'
                          }
                        }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={2}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 500
                              }}
                            >
                              {event.time}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {event.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {event.type.map((type, typeIndex) => (
                                <Chip
                                  key={typeIndex}
                                  label={type}
                                  size="small"
                                  sx={{ 
                                    bgcolor: 'primary.lighter',
                                    color: 'primary.main',
                                    fontSize: '0.75rem',
                                    fontWeight: 500
                                  }}
                                />
                              ))}
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <Box component="span" sx={{ opacity: 0.7 }}>Courtesies:</Box> {event.speaker}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <Box component="span" sx={{ opacity: 0.7 }}>Venue:</Box> {event.location}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={1} sx={{ textAlign: 'right' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpen(event.fullEvent)}
                              sx={{
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                '&:hover': {
                                  borderColor: 'primary.dark',
                                  bgcolor: 'primary.lighter'
                                },
                                minWidth: 'auto',
                                px: 2
                              }}
                            >
                              Details
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>

          {/* Event Details Modal */}
          <Modal 
            open={open} 
            onClose={handleClose}
            sx={{
              '& .MuiBackdrop-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <Box sx={{
              ...modalStyle,
              width: { xs: '90%', sm: 500 },
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              {selectedEvent && (
                <>
                  <Typography variant="h5" component="h2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {selectedEvent.title}
                  </Typography>
                  <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>Date & Time:</Box>
                      {format(selectedEvent.date, 'PPP p')}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>Location:</Box>
                      {selectedEvent.location}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>Organizer:</Box>
                      {selectedEvent.organizer}
                    </Typography>
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Event Description
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {selectedEvent.description}
                  </Typography>
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                      variant="contained" 
                      onClick={handleClose}
                      sx={{
                        bgcolor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.dark' },
                        px: 3
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Modal>
        </Container>
      </Box>
    </Box>
  );
}

export default Events;
